import { cn } from '@/lib/utils';
import { motion, useReducedMotion } from 'framer-motion';
import { PictureImage } from '@/components/ui/picture-image';
import { heroDesktopToMobile, heroMobileDimensions } from '@/lib/image-map';

interface PageHeroProps {
  imageSrc: string;
  mobileImageSrc?: string;
  eyebrow: string;
  headline: string;
  description?: string;
  className?: string;
  overlayOpacity?: number;
}

export function PageHero({ 
  imageSrc, 
  mobileImageSrc,
  eyebrow,
  headline,
  description, 
  className,
  overlayOpacity = 60 
}: PageHeroProps) {
  const prefersReducedMotion = useReducedMotion();
  const resolvedMobileImage = mobileImageSrc ?? heroDesktopToMobile[imageSrc];
  const mobileDimensions = resolvedMobileImage
    ? heroMobileDimensions[resolvedMobileImage]
    : undefined;

  return (
    <section className={cn("relative w-full py-24 md:py-32 lg:py-40 flex items-center justify-center overflow-hidden", className)}>
      <div className="absolute inset-0 z-0">
        <PictureImage
          src={imageSrc}
          smallSrc={resolvedMobileImage}
          alt={headline}
          width={mobileDimensions?.width ?? 1200}
          height={mobileDimensions?.height ?? 630}
          fetchPriority="high"
          className="w-full h-full object-cover object-center"
        />
        <div 
          className="absolute inset-0 bg-[#1a3a1a]" 
          style={{ opacity: overlayOpacity / 100 }}
        ></div>
      </div>
      
      <motion.div
        className="relative z-10 text-center px-4 max-w-4xl mx-auto"
        initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
      >
        <h1 className="mb-5 text-[15px] font-semibold uppercase tracking-[0.15em] text-white/90 md:text-[18px] lg:text-[20px]">
          {eyebrow}
        </h1>
        <p className="mb-6 text-4xl font-bold leading-tight text-white md:text-5xl lg:text-6xl">
          {headline}
        </p>
        {description && (
          <motion.p
            className="text-white/90 text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {description}
          </motion.p>
        )}
      </motion.div>
    </section>
  );
}
